import './styles.scss';
import qs from 'querystring';
import { Button, Divider, Menu, MenuItem } from '@material-ui/core';
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown';
import React, { useCallback, useRef, useState } from 'react';
import {
  connectCurrentRefinements,
  CurrentRefinementsProvided,
} from 'react-instantsearch-core';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import {
  clearedFilters,
  changedFilterValues,
  appliedFilters,
  makeSelectProductListFiltersByAttribute,
  makeSelectRefinementItemsByAttribute,
} from '~redux/search/product';
import { deepEquals } from '~utils/compare';
import { useQuery } from '~view/hooks/route';
import { useProductSearchSelector } from '~view/widgets/ProductSearchProvider';

const Clear: React.FC<CurrentRefinementsProvided & { listId: string }> = ({
  listId,
  items,
  refine,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery().get('s');
  const handleClear = useCallback(() => {
    history.replace(
      `${history.location.pathname}${query ? `?s=${query}` : ''}`
    );
    if (items.length) {
      refine(items);
    }
    dispatch(clearedFilters(listId));
  }, [query, items]);

  return (
    <Button
      className="rounded-pill"
      variant="contained"
      disableElevation
      onClick={handleClear}
      color="primary"
    >
      クリア
    </Button>
  );
};

export const ClearRefinements = React.memo(connectCurrentRefinements(Clear));

const RefinementMenu: React.FC<{
  listId: string;
  title: string;
  attribute: string;
  listItems: Record<string, string>;
  refine?: (items: string[]) => void;
}> = React.memo(({ listId, attribute, title, listItems, refine }) => {
  const [isOpen, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const refinedItems = useProductSearchSelector(
    makeSelectProductListFiltersByAttribute(attribute),
    listId
  );

  const handleCheck = useCallback(
    (e, item?: string) => {
      setSelectedItems((selectedItems) => {
        if (!item) {
          return Object.keys(listItems).length !== selectedItems.length
            ? Object.keys(listItems)
            : [];
        }
        if (selectedItems.includes(item)) {
          return selectedItems.filter((selectedItem) => selectedItem !== item);
        }
        return [...selectedItems, item];
      });
    },
    [listItems]
  );

  const dispatch = useDispatch();
  const applyFilters = useCallback(
    (items: string[]) => {
      dispatch(appliedFilters({ listId, attribute, items }));
    },
    [attribute]
  );

  const handleClear = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const history = useHistory();
  const handleApply = useCallback(() => {
    const decoded = qs.decode(history.location.search.substring(1));
    const search = qs.encode({
      ...decoded,
      page: 1,
      [attribute]: selectedItems,
    });
    window.history.replaceState(
      null,
      '',
      `${history.location.pathname}?${search}`
    );
    applyFilters(selectedItems);
    refine?.(selectedItems.map((item) => listItems[item]));
    setOpen(false);
  }, [selectedItems, attribute, listItems, listId]);

  const toggle = useCallback(() => {
    setSelectedItems(refinedItems);
    setOpen((prev) => !prev);
  }, [refinedItems]);

  // const location = useLocation();
  // useEffect(() => {
  //   const decoded = qs.decode(location.search.substring(1));
  //   const filters: string[] = [];
  //   const item = decoded[attribute];
  //   if (typeof item === 'string') {
  //     filters.push(item);
  //   } else if (item) {
  //     filters.push(...item);
  //   }
  //   setFilters(filters);
  //   if (refine) {
  //     refine(filters.map((item) => items[item] ?? item));
  //   }
  //   setSelectedItems(filters);
  // }, [location.search]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Button
        className="rounded-pill d-flex align-items-center"
        variant="contained"
        disableElevation
        ref={buttonRef}
        onClick={toggle}
        color={!refinedItems.length ? 'default' : 'primary'}
      >
        {title}
        <FiChevronDown className="ml-2" />
      </Button>
      <Menu
        anchorEl={buttonRef.current}
        open={isOpen}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={toggle}
        transitionDuration={100}
      >
        {Object.keys(listItems).map((label) => (
          <MenuItem key={label} onMouseDown={(e) => handleCheck(e, label)}>
            <input
              type="checkbox"
              name={label}
              className="mr-2"
              checked={selectedItems.includes(label)}
            />
            <span>{label}</span>
          </MenuItem>
        ))}
        <Divider />
        <div className="d-flex justify-content-end align-items-center p-2">
          <Button
            className="mr-2"
            onClick={handleClear}
            variant="contained"
            disableElevation
          >
            クリア
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disableElevation
            color="primary"
          >
            検索
          </Button>
        </div>
      </Menu>
    </>
  );
});

type ProductRefinementListProps = {
  listId: string;
  title: string;
  attribute: string;
  items?: Record<string, string>;
};

const ProductRefinementList: React.FC<ProductRefinementListProps> = ({
  listId,
  title,
  attribute,
  items,
}) => {
  const dispatch = useDispatch();
  const refinementItems = useProductSearchSelector(
    makeSelectRefinementItemsByAttribute(attribute),
    listId,
    deepEquals
  );
  const refine = useCallback(
    (filters: string[]) => {
      dispatch(changedFilterValues({ listId, attribute, items: filters }));
      return;
    },
    [attribute, listId, items]
  );

  return (
    <RefinementMenu
      listId={listId}
      attribute={attribute}
      title={title}
      listItems={
        items ??
        refinementItems?.reduce((prev, item) => {
          prev[item] = item;
          return prev;
        }, {} as Record<string, string>) ??
        {}
      }
      refine={items ? refine : undefined}
    />
  );
};

export default ProductRefinementList;
